import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { IconButton } from 'atoms/buttons/IconButton';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LinkOther, LinkOverlay } from 'atoms/navigation/Box';
import { BodyS } from 'atoms/typography';
import { ellipsis } from 'atoms/typography/Ellipsis';
import { PageHeader } from 'components/navigation/PageHeader';
import { Avatar } from 'components/user/Avatar';
import { DiscordUser } from 'components/user/DiscordUser';
import { TwitterUser } from 'components/user/TwitterUser';
import {
  ANY_SPORT_MY_CLUB_SLUG,
  FOOTBALL_PLAY,
} from 'constants/__generated__/routes';
import { useSportContext } from 'contexts/sport';
import { useSafePreviousNavigate } from 'hooks/useSafePreviousNavigate';
import { glossary } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

import type { MyClubPageHeader_user } from './__generated__/index.graphql';

const Wrapper = styled(Horizontal)`
  width: fit-content;
`;

const Nickname = styled.p`
  ${ellipsis}
  font: var(--t-bold) var(--t-label-m);
  @media ${tabletAndAbove} {
    font: var(--t-bold) var(--t-label-l);
  }
`;

type Props = {
  user: MyClubPageHeader_user;
};

export const MyClubPageHeader = ({ user }: Props) => {
  const { formatMessage } = useIntl();
  const { generateSportContextPath } = useSportContext();
  const safePreviousNavigate = useSafePreviousNavigate(FOOTBALL_PLAY);

  return (
    <PageHeader>
      <Horizontal gap={0.5}>
        <LinkOther as="span">
          <IconButton
            size="medium"
            color="transparent"
            onClick={safePreviousNavigate}
            icon={faChevronLeft}
            aria-label={formatMessage(glossary.back)}
          />
        </LinkOther>
        <Wrapper gap={1.5} center>
          <Avatar user={user} size={4.5} rounded />
          <Vertical gap={0.5}>
            <LinkOverlay
              as={Link}
              to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG, {
                params: {
                  slug: user.slug || '',
                },
              })}
            >
              <Nickname>{user.nickname}</Nickname>
            </LinkOverlay>
            <BodyS as="div">
              <LinkOther as="div">
                <Horizontal gap={2}>
                  <TwitterUser userProfile={user.profile} />
                  <DiscordUser userProfile={user.profile} />
                </Horizontal>
              </LinkOther>
            </BodyS>
          </Vertical>
        </Wrapper>
      </Horizontal>
    </PageHeader>
  );
};

MyClubPageHeader.fragments = {
  user: gql`
    fragment MyClubPageHeader_user on User {
      slug
      nickname
      profile {
        id
        ...TwitterUser_userProfile
        ...DiscordUser_userProfile
      }
      ...Avatar_user
    }
    ${Avatar.fragments.user}
    ${TwitterUser.fragments.userProfile}
    ${DiscordUser.fragments.userProfile}
  ` as TypedDocumentNode<MyClubPageHeader_user>,
};
